/**
 * PLAT-10920 — the settings-panel adapter for the AI text control.
 *
 * The sibling of the quick-edit popover's adapter, and deliberately the only
 * thing this ticket adds: `AITextControl` is presentation only (E1 / PLAT-10913
 * DS1), so a second surface is a props mapping rather than a second
 * implementation. Where the popover maps a `CompositionQuickEditField` onto the
 * component's contract, this maps a `ControlConfiguration` — same component,
 * same behaviour, more room.
 *
 * Two things ride together here:
 *
 *  - the ordinary **text field** for the property, because the generated value
 *    stays a hand-editable text property (**E2**). Without it an `aiText`
 *    control in the panel would be the one surface where the creator cannot
 *    type over what the model wrote — the popover renders the control BELOW its
 *    textarea for exactly this reason;
 *  - the AI control itself, wired to the surface's generation.
 *
 * The generation comes from the mounting surface (`CompositionControls` builds
 * it once for the panel). A surface that wires none — the Quote Card settings
 * panel, which owns its own renderer and editor — never renders this adapter at
 * all; `CompositionControl` falls back to the plain text field there (DS4).
 */
import React, { useMemo } from 'react';

import { AITextControl } from './AITextControl';
import { TextControl } from './TextControl';

import type {
  AITextControlConfiguration,
  ControlConfiguration,
} from '../types';
import type { AITextTranscriptElement } from './useAITextBaseText';
import type { AITextGeneration } from './useAITextGeneration';
import type { TimelineMarker } from '@src/components/transcript/hooks/useTimelineMarkers';
import type { ElementState } from '@src/renderer/ElementState';

/**
 * What a settings panel has to supply for its `aiText` controls to generate.
 * Built once per panel — the marker and the transcript are properties of the
 * selected graphic, not of the individual control.
 */
export interface AITextPanelContext {
  generation: AITextGeneration;
  /** The story / clip transcript the base text resolves from. */
  transcriptElements: ReadonlyArray<AITextTranscriptElement> | null | undefined;
  /**
   * The selected graphic as the marker `findOverlayRanges` maps from. Carries
   * the placement's `transcriptAnchor` when it has one and its time window
   * otherwise, so a composition added from the design grid still answers
   * "selection" (DS3).
   */
  overlay: TimelineMarker | null;
}

interface Props {
  control: AITextControlConfiguration;
  activeElement: ElementState;
  modifyProperty: (control: ControlConfiguration, value: unknown) => void;
  compositionMeta?: Record<string, unknown>;
  context: AITextPanelContext;
}

export const AITextControlPanelAdapter: React.FC<Props> = ({
  control,
  activeElement,
  modifyProperty,
  compositionMeta,
  context,
}) => {
  const { generation, transcriptElements, overlay } = context;
  const elementId = String(activeElement.source.id ?? '');
  // The same identity the quick-edit extraction uses (`control.id`), because
  // the provenance blob both surfaces read is keyed by it. A control with no
  // id can't carry provenance at all — `CompositionControl` renders the plain
  // text field for those rather than an AI control that can never remember a
  // run.
  const controlId = control.id ?? '';

  const field = useMemo(
    () => ({
      controlId,
      property: control.property,
      elementPath: control.elementPaths?.[0] ?? [],
      aiText: { promptId: control.promptId },
    }),
    [controlId, control.property, control.elementPaths, control.promptId],
  );

  // Read LIVE on every render, exactly as the popover does: the blob is mutated
  // in place by a generation and by a selection, so anything cached would leave
  // the picker empty right after Generate resolves.
  const provenance = generation.getProvenance(elementId, controlId);
  const status = generation.getStatus(elementId, controlId);

  return (
    <>
      <TextControl
        control={control}
        activeElement={activeElement}
        modifyProperty={modifyProperty}
        compositionMeta={compositionMeta}
      />
      <AITextControl
        /*
           Remount per graphic. The panel does NOT unmount between selections —
           `CompositionSettings` swaps `activeElement` on the same
           `CompositionControls` instance — and two Smart Graphics cards both
           key on `hook`, so without this React carries the base-text scope,
           the typed instructions and the expanded state onto the next
           graphic. Same reason the two quick-edit surfaces key their popover
           on the marker id.
        */
        key={elementId}
        idSuffix={`panel-${controlId}`}
        label={control.label}
        // The LAST RUN's scope wins over the control's configured default
        // (PLAT-10917 DS2): the blob records what produced the text now on the
        // canvas, and reopening on the DatoCMS default would quietly change
        // scope on the next Regenerate.
        defaultBaseTextScope={
          provenance?.baseTextScope ?? control.defaultBaseTextScope
        }
        overlay={overlay}
        transcriptElements={transcriptElements}
        onGenerate={request =>
          void generation.generateForField({
            elementId,
            field,
            baseTextScope: request.baseTextScope,
            overlay,
            instructions: request.instructions,
          })
        }
        {...status}
        options={provenance?.options}
        selectedOption={provenance?.selectedOption}
        defaultInstructions={provenance?.instructions}
        // PLAT-10918 — the two halves of the staleness compare, passed
        // straight through. Nothing about staleness lives in this adapter.
        sourceFingerprint={provenance?.sourceFingerprint}
        generatedBaseTextScope={provenance?.baseTextScope}
        onSelectOption={optionIndex =>
          generation.selectOptionForField({ elementId, field, optionIndex })
        }
        // PLAT-10923 — the control fires the base-text switch and the staleness
        // indicator itself; this is the identity it stamps them with.
        analytics={generation.analytics(elementId, controlId)}
      />
    </>
  );
};
